/**
 * 設定 → 見積・請求 → メールの文面（#225、docs/91 §3.5・§5）：書類の種類（見積書・請求書・領収書・見積依頼書）ごとに会社の既定の件名・本文を1つずつ。
 *   ・iOS 風（docs/10 §9.15）。上の帯「‹ 見積・請求｜自動で保存されます」、大きな題「メールの文面」と書類の名前
 *   ・件名・本文（差し込みの札を押すとカーソルの所に入る）・「既定に戻す」（会社が直しているときだけ）。自動で保存（lib/autoSave.tsx。欄を離れたとき）
 *   ・見積依頼書は設定「見積依頼」と同じ件名・本文（tenants.rfq_settings＝置き場は1つ）。差し込みは見積依頼の札（［番号］［回答期限］…）
 *   ・本文・件名に URL は書けない（閲覧ページは［閲覧ページ］で差し込む。contract の MailTemplatesUpdate）
 *   ・書くのは管理者（担当者は見るだけ）
 */
import { t } from "../labels.js";
import { MAIL_FIELDS, MAIL_FIELDS_DECLINE, MAIL_FIELDS_NOTICE, MAIL_FIELDS_ORDER, MAIL_FIELDS_REVISE, MAIL_KIND_LABEL, MAIL_LIMITS, MAIL_MSG as M, MailKind, MailTemplatesUpdate, RFQ_MAIL_FIELDS, type MailTemplates } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { Navigate, useParams } from "react-router";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { BackButton, useBackNav, useLeaveGuard, usePageName } from "../components/BackButton.jsx";
import { GroupDangerRow, GroupList, GroupSection } from "../components/GroupList.jsx";
import { ErrorText, Field, Help, useToast } from "../components/ui.jsx";
import { AutoSaveNote, AutoSaveStatus, useAutoSave } from "../lib/autoSave.jsx";

const KEY = ["mail-templates"];
type Text = { subject: string; body: string };

export function SettingsMailTemplatePage() {
  const { kind: raw = "" } = useParams();
  const kind = MailKind.safeParse(raw);
  if (!kind.success) return <Navigate to="/settings/estimate" replace />;
  return <MailTemplateEditor kind={kind.data} />;
}

function MailTemplateEditor({ kind }: { kind: MailKind }) {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const q = useQuery({ queryKey: KEY, queryFn: () => api<MailTemplates>("GET", "/company/mail-templates") });
  const cur = q.data?.[kind];
  const a = useAutoSave<Text>({
    server: cur ? { subject: cur.subject, body: cur.body } : null,
    enabled: admin,
    send: async (v) => {
      const r = await api<MailTemplates>("PUT", "/company/mail-templates", { [kind]: v } satisfies MailTemplatesUpdate);
      qc.setQueryData(KEY, r);
      return { subject: r[kind].subject, body: r[kind].body };
    },
    check: (v) => {
      const r = MailTemplatesUpdate.safeParse({ [kind]: v });
      return r.success ? null : { status: M.urlNotAllowed, errors: { text: r.error.issues[0]?.message ?? M.urlNotAllowed } };
    },
    hide: (v) => ({ url: "/api/v1/company/mail-templates", method: "PUT", body: { [kind]: v } }),
  });
  const back = useBackNav({ to: "/settings/estimate", label: "見積・請求" }, a.unsent);
  const guard = useLeaveGuard(a.unsent);
  usePageName(M.settingsSection);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const reset = useMutation({
    mutationFn: () => api<MailTemplates>("PUT", "/company/mail-templates", { [kind]: null } satisfies MailTemplatesUpdate),
    onSuccess: (r) => {
      qc.setQueryData(KEY, r);
      a.replace({ subject: r[kind].subject, body: r[kind].body });
      toast(M.settingsResetDone);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  const f = a.form;
  if (!q.data || !f || !me.data) return <p className="hint">読み込んでいます…</p>;
  const fields = kind === "rfq" ? RFQ_MAIL_FIELDS : kind === "order" ? MAIL_FIELDS_ORDER : kind === "revise" ? MAIL_FIELDS_REVISE : kind === "decline" ? MAIL_FIELDS_DECLINE : kind === "payment_notice" ? MAIL_FIELDS_NOTICE : MAIL_FIELDS;
  const insert = (tag: string) => {
    const el = bodyRef.current;
    const at = el ? el.selectionStart : f.body.length;
    const end = el ? el.selectionEnd : at;
    a.set((v) => ({ ...v, body: v.body.slice(0, at) + tag + v.body.slice(end) }));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + tag.length, at + tag.length);
    });
  };
  const err = a.errors.text ?? a.serverError;
  return (
    <div className="ppage estpage" data-testid="mail-template-page" {...a.formProps}>
      <div className="cotop">
        <BackButton back={back} />
        <span className="cotop-sp" />
        {admin ? <AutoSaveStatus state={a.state} testId="mail-template-autosave" /> : null}
      </div>
      {back.dialog}
      {guard.dialog}
      <h1 className="ptitle">
        {M.settingsSection}
        <Help testId="help-mail-template" label={`${M.settingsSection}の説明`} text={M.settingsHelp} />
      </h1>
      <AutoSaveNote state={a.state} testId="mail-template-autosave-note" />
      <GroupSection title={t(MAIL_KIND_LABEL[kind])} data-testid="mail-template-sec">
        <div className="mltpl">
          <Field id="mlt_s" label={M.subject}>
            <input id="mlt_s" className="inp" placeholder=" " value={f.subject} maxLength={MAIL_LIMITS.templateSubject} disabled={!admin} onChange={(e) => a.set((v) => ({ ...v, subject: e.target.value }), { wait: true })} data-testid="mail-template-subject" />
          </Field>
          <Field id="mlt_b" label={M.body} help={<Help label={M.fields} text={M.fieldsHelp} testId="help-mail-template-fields" />}>
            <textarea id="mlt_b" ref={bodyRef} className="inp" rows={12} placeholder=" " value={f.body} maxLength={MAIL_LIMITS.templateBody} disabled={!admin} onChange={(e) => a.set((v) => ({ ...v, body: e.target.value }), { wait: true })} data-testid="mail-template-body" />
          </Field>
          {admin ? (
            <div className="mlfields" role="group" aria-label={M.fields} data-testid="mail-template-fields">
              {fields.map((x) => (
                <button key={x} type="button" className="btn tonal sm" onClick={() => insert(x)} data-testid="mail-template-field">
                  {x}
                </button>
              ))}
            </div>
          ) : null}
          {err ? (
            <p className="gsecerr" role="alert" data-testid="mail-template-error">
              {`⚠ ${err}`}
            </p>
          ) : null}
        </div>
      </GroupSection>
      {admin && q.data[kind].source === "tenant" ? (
        <GroupSection>
          <GroupList plain>
            <GroupDangerRow tone="ai" title={M.settingsReset} onClick={() => reset.mutate()} disabled={reset.isPending} buttonTestId="mail-template-reset" />
          </GroupList>
        </GroupSection>
      ) : null}
      {node}
    </div>
  );
}
